import { get } from 'lodash';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import useEventMetricsQuery, { createEventMetricsQuery } from './useEventMetricsQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { panelsAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { EVENT_METRICS_LIST } from '@src/hooks/event/useEventConfigs';

/**
 * Hook to fetch metrics for a specific event
 * Uses signal_name_param instead of event filter
 */
function useEventMetricsData({ eventName, eventMeta }: { eventName: string; eventMeta?: any }) {
  const panels = useAtomValue(panelsAtom);
  const { interval, filter, _rKey } = useQueryParams();

  const isDeleted = eventMeta?.isDeleted;

  // Use base filter only (no event filter)
  const options = {};
  const payload = createEventMetricsQuery(
    eventName,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    filter || [],
    options,
  );

  const result = useEventMetricsQuery({
    eventName: eventName,
    queryKey: ['overview', 'event', eventName, payload.queries, _rKey],
    queryFn: () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        const formattedData = formatTimeseriesData({
          metrics: EVENT_METRICS_LIST,
          data,
          interval,
          annotations,
        });
        return { data: formattedData, totals };
      }),
    options: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false, // Prevent refetch when panel is shown
      enabled: !isDeleted && panels.events,
      keepPreviousData: true, // Preserve data when panel is hidden/shown
      staleTime: 5 * 60 * 1000, // 5m
    },
  });

  if (isDeleted) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
      queued: false,
    };
  }

  return result;
}

export default useEventMetricsData;
